/*
 * Click spark, ported from RareUI (https://www.rareui.com), MIT License,
 * Copyright (c) 2025 Swapnil Kalambe. See THIRD_PARTY_NOTICES.md.
 * Change: fires only when code calls spark(x, y) (for example on Add to basket),
 * not on every click on the page, and the canvas sleeps when idle.
 */
import { useEffect, useRef } from "react";
import { motionAllowed } from "./motionEnv";

const EVT = "kj:spark";
export function spark(x, y) {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(EVT, { detail: { x, y } }));
}
export function sparkFromEvent(e) {
  if (e?.clientX || e?.clientY) spark(e.clientX, e.clientY);
  else if (e?.currentTarget?.getBoundingClientRect) {
    const r = e.currentTarget.getBoundingClientRect();
    spark(r.left + r.width / 2, r.top + r.height / 2);
  }
}

export default function ClickSpark({ color = "#2E7D4F", size = 10, radius = 22, count = 10, duration = 450 }) {
  const canvas = useRef(null);
  useEffect(() => {
    if (!motionAllowed()) return undefined;
    const c = canvas.current;
    const ctx = c?.getContext("2d");
    if (!ctx) return undefined;
    let sparks = [];
    let raf = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => { c.width = window.innerWidth * dpr; c.height = window.innerHeight * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    resize();
    const draw = (t) => {
      ctx.clearRect(0, 0, c.width, c.height);
      sparks = sparks.filter((s) => {
        const p = (t - s.t) / duration;
        if (p >= 1) return false;
        const e = p * (2 - p);
        const d = e * radius;
        const len = size * (1 - e);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(s.x + d * Math.cos(s.a), s.y + d * Math.sin(s.a));
        ctx.lineTo(s.x + (d + len) * Math.cos(s.a), s.y + (d + len) * Math.sin(s.a));
        ctx.stroke();
        return true;
      });
      raf = sparks.length ? requestAnimationFrame(draw) : 0;
    };
    const onSpark = (ev) => {
      const now = performance.now();
      for (let i = 0; i < count; i++) sparks.push({ x: ev.detail.x, y: ev.detail.y, a: (2 * Math.PI * i) / count, t: now });
      if (!raf) raf = requestAnimationFrame(draw);
    };
    window.addEventListener(EVT, onSpark);
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener(EVT, onSpark); window.removeEventListener("resize", resize); cancelAnimationFrame(raf); };
  }, [color, size, radius, count, duration]);
  return <canvas ref={canvas} aria-hidden="true" style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 9999 }} />;
}
